// Copyright 2024 Sorama B.V.

import { PLATFORM } from "aurelia-pal";
import { GuidanceTopicType, IGuidanceTopic } from "catalogs/GuidanceCatalog";
import { WorkflowModuleType } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { WorkflowModes } from "services/WorkflowModes/WorkflowModes";

export class CAMiV64GuidanceCatalog {
    static CAMiV64GuidanceOverviewModule: IGuidanceTopic = {
        name: "CAMiV64GuidanceOverviewModule",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_Overview.html"), // https://sorama.atlassian.net/wiki/spaces/SI/pages/3808591879/Knowledge+base+articles+for+CAM+iV64s+Ex
        title: "Sorama CAM iV64s and CAM iV64Ex",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicType: GuidanceTopicType.TopicOverview,
        topicContent: "Sorama CAM iV64s and CAM iV64Ex",
    };

    static CAMiV64_PerformLeakInspection: IGuidanceTopic = {
        name: "CAMiV64_PerformLeakInspection",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_PerformLeakInspection.html"),
        title: "How to perform a leak inspection?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to perform a leak inspection?",
        catchwords: [""],
    };

    static CAMiV64_PerformPartialDischargeInspection: IGuidanceTopic = {
        name: "CAMiV64_PerformPartialDischargeInspection",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_PerformPartialDischargeInspection.html"),
        title: "How to perform a partial discharge inspection?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to perform a partial discharge inspection?",
        catchwords: [""],
    };

    static CAMiV64_PerformMechanicalInspection: IGuidanceTopic = {
        name: "CAMiV64_PerformMechanicalInspection",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_PerformMechanicalInspection.html"),
        title: "How to perform a mechanical inspection?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to perform a mechanical inspection?",
        catchwords: [""],
    };

    static CAMiV64_UseFrequencySelection: IGuidanceTopic = {
        name: "CAMiV64_UseFrequencySelection",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_UseFrequencySelection.html"),
        title: "How to use the frequency selection?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to use the frequency selection?",
        catchwords: [""],
    };

    static CAMiV64_AddNotesToMeasurement: IGuidanceTopic = {
        name: "CAMiV64_AddNotesToMeasurement",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_AddNotesToMeasurement.html"),
        title: "How to add notes to your measurement?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to add notes to your measurement?",
        catchwords: [""],
    };

    static CAMiV64_AddTagsToMeasurement: IGuidanceTopic = {
        name: "CAMiV64_AddTagsToMeasurement",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_AddTagsToMeasurement.html"),
        title: "How to add tags to your measurement?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to add tags to your measurement?",
        catchwords: [""],
    };

    static CAMiV64_AddImagesToMeasurement: IGuidanceTopic = {
        name: "CAMiV64_AddImagesToMeasurement",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_AddImagesToMeasurement.html"),
        title: "How to add photos to your measurement?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to add photos to your measurement?",
        catchwords: [""],
    };

    static CAMiV64_DownloadReport: IGuidanceTopic = {
        name: "CAMiV64_DownloadReport",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_DownloadReport.html"),
        title: "How to send a report from the device to your email?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to send a report from the device to your email?",
        catchwords: [""],
    };

    static CAMiV64_CreateProject: IGuidanceTopic = {
        name: "CAMiV64_CreateProject",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_CreateProject.html"),
        title: "How to create a project?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to create a project?",
        catchwords: [""],
    };

    static CAMiV64_SelectProject: IGuidanceTopic = {
        name: "CAMiV64_SelectProject",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_SelectProject.html"),
        title: "How to select a different project?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to select a different project?",
        catchwords: [""],
    };

    static CAMiV64_DeleteProject: IGuidanceTopic = {
        name: "CAMiV64_DeleteProject",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_DeleteProject.html"),
        title: "How to delete a project or measurement?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to delete a project or measurement?",
        catchwords: [""],
    };

    static CAMiV64_ChangeAcousticSettings: IGuidanceTopic = {
        name: "CAMiV64_ChangeAcousticSettings",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_ChangeAcousticSettings.html"),
        title: "How to change my acoustic settings?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to change my acoustic settings?",
        catchwords: [""],
    };

    static CAMiV64_TypesOfOptionsAcousticMenu: IGuidanceTopic = {
        name: "CAMiV64_TypesOfOptionsAcousticMenu",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_TypesOfOptionsAcousticMenu.html"),
        title: "What is each type of option in the acoustics menu?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "What is each type of option in the acoustics menu?",
        catchwords: [""],
    };

    static CAMiV64_ToggleWiFi: IGuidanceTopic = {
        name: "CAMiV64_ToggleWiFi",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_ToggleWiFi.html"),
        title: "How to turn on the Wi-Fi?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to turn on the Wi-Fi?",
        catchwords: [""],
    };

    static CAMiV64_ShareScreen: IGuidanceTopic = {
        name: "CAMiV64_ShareScreen",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_ShareScreen.html"),
        title: "How to share my device screen?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to share my device screen?",
        catchwords: [""],
    };

    static CAMiV64_InstallLicenses: IGuidanceTopic = {
        name: "CAMiV64_InstallLicenses",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_InstallLicenses.html"),
        title: "How to install licenses?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to install licenses?",
        catchwords: [""],
    };

    static CAMiV64_CheckLicenses: IGuidanceTopic = {
        name: "CAMiV64_CheckLicenses",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_CheckLicenses.html"),
        title: "How to check for available licenses?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to check for available licenses?",
        catchwords: [""],
    };

    static CAMiV64_ResetDevice: IGuidanceTopic = {
        name: "CAMiV64_ResetDevice",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_ResetDevice.html"),
        title: "How to reset my device?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to reset my device?",
        catchwords: [""],
    };

    static CAMiV64_ChangeLanguage: IGuidanceTopic = {
        name: "CAMiV64_ChangeLanguage",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_ChangeLanguage.html"),
        title: "How to change the language of my device?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to change the language of my device?",
        catchwords: [""],
    };

    static CAMiV64_ConnectToSoramaPortal: IGuidanceTopic = {
        name: "CAMiV64_ConnectToSoramaPortal",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_ConnectToSoramaPortal.html"),
        title: "How to connect my device to the Sorama Portal?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to connect my device to the Sorama Portal?",
        catchwords: [""],
    };

    static CAMiV64_UpdateFirmware: IGuidanceTopic = {
        name: "CAMiV64_UpdateFirmware",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_UpdateFirmware.html"),
        title: "How to update my firmware?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to update my firmware?",
        catchwords: [""],
    };

    static CAMiV64_ActivatePackage: IGuidanceTopic = {
        name: "CAMiV64_ActivatePackage",
        modulePath: PLATFORM.moduleName("guidance/CAMiV64/CAMiV64_ActivatePackage.html"),
        title: "How to activate my package?",
        moduleType: WorkflowModuleType.Help,
        relevantWorkflowModes: [WorkflowModes.FullWFM, WorkflowModes.IndustrialHandheld],
        topicParent: CAMiV64GuidanceCatalog.CAMiV64GuidanceOverviewModule,
        topicType: GuidanceTopicType.Question,
        topicContent: "How to activate my package?",
        catchwords: [""],
    };
}
